import { debounce, find, flatMapDeep, isEmpty, isObject, sortBy } from "lodash";
import { useRouter } from "next/router";
import { TreeSelect } from "primereact/treeselect";
import { classNames } from "primereact/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import usePermissions from "../../../../common/api/usePermissions";
import { FadeIn, Skeleton } from "../../../../common/components";
import {
    ANY_LOCATION_ID,
    COMPANY_ROUTES,
    LOCATION_ROUTES,
    PERMISSIONS,
} from "../../../../common/constants";
import { useAnalyzeStore } from "../../../analyze/store";
import { getAnalyzeTabQuery } from "../../../analyze/utils";
import type { Company } from "../../../users/models/company.model";
import type { PanelTreeNode } from "./templates";
import { PanelTemplate, ValueTemplate } from "./templates";

const SEARCH_DEBOUNCE_MS = 1000;

const ALL_COMPANY_ROUTES = flatMapDeep(COMPANY_ROUTES, (value) =>
    isObject(value) ? flatMapDeep(value) : value
);
const ALL_LOCATION_ROUTES = flatMapDeep(LOCATION_ROUTES, (value) =>
    isObject(value) ? flatMapDeep(value) : value
);

interface LocationSelectionProps {
    companies: Company[];
    isLoadingCompanies: boolean;
}

const LocationSelection = (props: LocationSelectionProps) => {
    const { companies, isLoadingCompanies } = props;

    const router = useRouter();
    const { t } = useTranslation("common");
    const { companyPermissions, hasPermissions } = usePermissions();

    const { companyId, locationId } = router.query as {
        companyId?: string;
        locationId?: string;
    };

    const [options, setOptions] = useState<PanelTreeNode[] | undefined>(
        undefined
    );
    const [search, setSearch] = useState<string>("");

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const treeSelectRef = useRef<TreeSelect | null>(null);

    // useMemo required for a stable reference for the debounce
    const allOptions: PanelTreeNode[] = useMemo(
        () =>
            sortBy(companies, (company) => company?.name?.toLowerCase()).map(
                (company) => ({
                    id: company.id,
                    name: company.name,
                    isExpanded: company.id === companyId,
                    children: sortBy(company.locations || [], (location) =>
                        location.name.toLowerCase()
                    ).map((location) => ({
                        id: location.id,
                        name: location.name,
                        city: location.address?.city,
                        locationType: location.locationType,
                    })),
                })
            ),
        [companies, companyId]
    );

    const scrollToTop = () =>
        requestAnimationFrame(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0;
            }
        });

    // useMemo required so debounce timer persists across renders
    const handleFilterOptions = useMemo(
        () =>
            debounce((searchValue: string) => {
                if (allOptions.length === 0) return;

                if (searchValue === "") {
                    setOptions(allOptions);
                    scrollToTop();
                    return;
                }

                const lowerSearch = searchValue.toLowerCase();
                const newOptions = allOptions
                    .map((company) => {
                        if (
                            company.name?.toLowerCase().includes(lowerSearch) ||
                            company.id.toLowerCase() === lowerSearch
                        ) {
                            return { ...company, isExpanded: true };
                        }

                        const locations = company.children?.filter(
                            (location) =>
                                location.name
                                    ?.toLowerCase()
                                    .includes(lowerSearch) ||
                                location.id.toLowerCase() === lowerSearch
                        );

                        return !isEmpty(locations)
                            ? {
                                  ...company,
                                  isExpanded: true,
                                  children: locations,
                              }
                            : null;
                    })
                    .filter(Boolean) as PanelTreeNode[];

                setOptions(newOptions);
                scrollToTop();
            }, SEARCH_DEBOUNCE_MS),
        [allOptions]
    );

    // Cancel any pending debounce calls on unmount
    useEffect(() => {
        return () => {
            handleFilterOptions.cancel();
        };
    }, [handleFilterOptions]);

    const handleNavigation = (
        targetCompanyId: string,
        targetLocationId?: string
    ) => {
        // Until Company Dashboard page will be added; check done for Technical User
        const companyFallback = hasPermissions({
            permissions: [PERMISSIONS.COMPANY.READ],
            companyId,
            locationId: ANY_LOCATION_ID,
        })
            ? COMPANY_ROUTES.LOCATIONS
            : COMPANY_ROUTES.ENERGY.GENERAL;
        const locationFallback = LOCATION_ROUTES.DASHBOARD;

        const current = router.pathname;
        let nextPath: string;
        if (targetLocationId) {
            nextPath = ALL_LOCATION_ROUTES.includes(current)
                ? current
                : locationFallback;
        } else if (
            current === COMPANY_ROUTES.ENERGY.RULESET ||
            current === COMPANY_ROUTES.ENERGY.RULE
        ) {
            nextPath = COMPANY_ROUTES.ENERGY.RULESETS;
        } else {
            nextPath = ALL_COMPANY_ROUTES.includes(current)
                ? current
                : companyFallback;
        }

        router
            .push({
                pathname: companyId ? nextPath : locationFallback,
                query: {
                    companyId: targetCompanyId,
                    ...(targetLocationId && { locationId: targetLocationId }),
                    ...getAnalyzeTabQuery(
                        nextPath,
                        useAnalyzeStore.getState().tabId
                    ),
                },
            })
            .then(() => treeSelectRef.current?.hide());

        setSearch("");
    };

    const hasCompanyAccess = (id: string) => {
        if (hasPermissions()) return true;
        const entry = find(companyPermissions, { id });
        return (
            !isEmpty(entry?.permissions) || !isEmpty(entry?.locationPermissions)
        );
    };

    const handleSelectCompany = (id: string) => {
        if (!hasCompanyAccess(id)) return;
        handleNavigation(id);
    };

    const handleToggleExpand = (id: string) =>
        setOptions((prev) =>
            prev?.map((company) =>
                company.id === id
                    ? { ...company, isExpanded: !company.isExpanded }
                    : company
            )
        );

    const handleResetPanel = () => {
        handleFilterOptions.cancel();
        setSearch("");
        setOptions(allOptions);
    };

    const handleShowPanel = () => {
        setOptions(allOptions);
        requestAnimationFrame(() => searchInputRef.current?.focus()); // Focus search input when menu opens
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        handleFilterOptions(value);
    };

    const hasValidSelection = companies.some(
        (company) => company.id === companyId
    );

    const isCompanyView = !!companyId && !locationId;

    if (isLoadingCompanies) {
        return (
            <Skeleton width="8rem" height="1.75rem" borderRadius="0.375rem" />
        );
    }

    return (
        <FadeIn className="min-w-0 pr-1 md:p-0">
            <TreeSelect
                ref={treeSelectRef}
                options={[]}
                disabled={
                    isLoadingCompanies || !router.isReady || !hasValidSelection
                }
                className={classNames(
                    "min-w-0 border! [&.p-focus]:shadow-none! w-full max-w-72 min-h-11 md:min-h-0 h-auto! md:h-7! rounded-lg md:rounded-md border-transparent! hover:border-transparent! bg-transparent! hover:bg-surface-500/15! max-md:active:bg-surface-500/26! [&:has([aria-expanded=true])]:bg-surface-500/15!",
                    {
                        "md:bg-primary-500! md:hover:bg-primary-600! md:[&:has([aria-expanded=true])]:bg-primary-600!":
                            !isCompanyView,
                    }
                )}
                pt={{
                    wrapper: { className: "hidden" },
                    header: { className: "hidden" },
                    panel: {
                        className:
                            "w-72 rounded-lg border border-surface-555 shadow-md p-1.5",
                    },
                    label: {
                        className:
                            "min-w-0 [&>div]:truncate [&>div]:w-full [&>div]:max-w-full py-0! pl-2! pr-2! md:pr-0! text-[0.9375rem] md:text-base font-semibold leading-tight md:leading-normal",
                    },
                    trigger: {
                        className: "w-6.75! hidden! md:flex!",
                    },
                    triggerIcon: {
                        className: "size-2.75 text-primary-text!",
                    },
                }}
                // When a wrong id is typed in the URL show - as placeholder
                placeholder="-"
                panelFooterTemplate={() => (
                    <PanelTemplate
                        hasSingleCompany={companies.length === 1}
                        onCompanySelect={handleSelectCompany}
                        onLocationSelect={handleNavigation}
                        onSearchChange={handleSearchChange}
                        onToggleExpand={handleToggleExpand}
                        options={options ?? allOptions}
                        scrollContainerRef={scrollContainerRef}
                        search={search}
                        searchInputRef={searchInputRef}
                        selectedCompanyId={companyId}
                        selectedLocationId={locationId}
                    />
                )}
                valueTemplate={() => <ValueTemplate companies={companies} />}
                onShow={handleShowPanel}
                onHide={handleResetPanel}
                emptyMessage={t("common:noResultsFound")}
            />
        </FadeIn>
    );
};

export default LocationSelection;
